import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import config from "../../config";
import { Session, SessionStatus, Coachee } from "../../models";
import { SessionService } from "../../services/coachingNavigator/sessionService";
import { CoacheeService } from "../../services/coachingNavigator/coacheeService";
import { Context } from "../context";
import CreateClientPanel from "./createClientPanel";
import ConfirmationPanel from "../confirmationPanel";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (session: Session) => void;
  initialSession?: Session | null;
  onSaved?: (session: Session) => void;
}

const sessionService = new SessionService(config.api.baseUrl, "/sessions");
const coacheeService = new CoacheeService(config.api.baseUrl, "/coachee");

const defaultDuration = 60;

const CreateSessionPanel: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  initialSession,
  onSaved,
}) => {
  const { t } = useTranslation();
  const appContext = React.useContext(Context);
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState<number>(defaultDuration);
  const [location, setLocation] = useState("");
  const [remarks, setRemarks] = useState("");
  const [coachees, setCoachees] = useState<Coachee[]>([]);
  const [saving, setSaving] = useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  const initialFormState = useMemo(() => {
    if (!initialSession) {
      return {
        name: "",
        clientId: "",
        date: "",
        time: "",
        duration: defaultDuration,
        location: "",
        remarks: "",
      };
    }
    let initDate = "";
    let initTime = "";
    let initDuration = defaultDuration;
    const start = initialSession.startTime
      ? new Date(initialSession.startTime)
      : undefined;
    if (start && !Number.isNaN(start.getTime())) {
      initDate = start.toISOString().slice(0, 10);
      initTime = `${start.getUTCHours().toString().padStart(2, "0")}:${start.getUTCMinutes().toString().padStart(2, "0")}`;
    }
    const end = initialSession.endTime
      ? new Date(initialSession.endTime)
      : undefined;
    if (start && end && !Number.isNaN(end.getTime())) {
      initDuration = Math.max(
        5,
        Math.round((end.getTime() - start.getTime()) / 60000),
      );
    }
    return {
      name: initialSession.name || "",
      clientId: initialSession.clientId || "",
      date: initDate,
      time: initTime,
      duration: initDuration,
      location: initialSession.location || "",
      remarks:
        (initialSession.remarks && initialSession.remarks.join("\n")) || "",
    };
  }, [initialSession]);

  const resetForm = useCallback(() => {
    setName("");
    setClientId("");
    setDate("");
    setTime("");
    setDuration(defaultDuration);
    setLocation("");
    setRemarks("");
  }, []);

  const populateForm = useCallback(() => {
    if (!initialSession) {
      resetForm();
      return;
    }

    setName(initialSession.name || "");
    setClientId(initialSession.clientId || "");

    const start = initialSession.startTime
      ? new Date(initialSession.startTime)
      : undefined;
    if (start && !Number.isNaN(start.getTime())) {
      setDate(start.toISOString().slice(0, 10));
      const hoursStr = start.getUTCHours().toString().padStart(2, "0");
      const minutesStr = start.getUTCMinutes().toString().padStart(2, "0");
      setTime(`${hoursStr}:${minutesStr}`);
    } else {
      setDate("");
      setTime("");
    }

    const end = initialSession.endTime
      ? new Date(initialSession.endTime)
      : undefined;
    if (start && end && !Number.isNaN(end.getTime())) {
      const diffMinutes = Math.max(
        5,
        Math.round((end.getTime() - start.getTime()) / 60000),
      );
      setDuration(diffMinutes);
    } else {
      setDuration(defaultDuration);
    }

    setLocation(initialSession.location || "");
    setRemarks(
      (initialSession.remarks && initialSession.remarks.join("\n")) || "",
    );
  }, [initialSession, resetForm]);

  useEffect(() => {
    if (!open) return;

    populateForm();

    const creatorEmail = (appContext.state.invoker?.email || "").trim();
    if (!creatorEmail) {
      setCoachees([]);
      return;
    }

    coacheeService
      .getByCreator(creatorEmail)
      .then((data) =>
        setCoachees((data || []).filter((c) => c.active !== false)),
      )
      .catch((err) => console.error("Failed to load coachees", err));
  }, [open, populateForm]);

  const handleClientCreated = useCallback((coachee: Coachee) => {
    setCoachees((prev) => [...prev, coachee]);
    if (coachee.id) {
      setClientId(coachee.id.toString());
    }
    setCreateClientOpen(false);
  }, []);

  const hasChanges = useMemo(() => {
    return (
      name !== initialFormState.name ||
      clientId !== initialFormState.clientId ||
      date !== initialFormState.date ||
      time !== initialFormState.time ||
      duration !== initialFormState.duration ||
      location !== initialFormState.location ||
      remarks !== initialFormState.remarks
    );
  }, [
    name,
    clientId,
    date,
    time,
    duration,
    location,
    remarks,
    initialFormState,
  ]);

  const handleWarningClose = useCallback(() => {
    if (hasChanges) {
      setWarningDialogOpen(true);
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  const formatDurationDisplay = useCallback(
    (minutes: number) => {
      const hrs = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hrs === 0)
        return `${mins} ${t("coachingNavigator.minutes") || "Min."}`;
      if (mins === 0) return `${hrs} ${t("coachingNavigator.hours") || "Std."}`;
      return `${hrs} ${t("coachingNavigator.hours") || "Std."} ${mins} ${t("coachingNavigator.minutes") || "Min."}`;
    },
    [t],
  );

  const handleDurationChange = useCallback((increment: number) => {
    setDuration((prev) => {
      const next = prev + increment;
      if (next < 5 || next > 480) return prev;
      return next;
    });
  }, []);

  const incrementBookedSessions = useCallback(
    async (targetClientId: string) => {
      if (!targetClientId) return;

      try {
        const coachee = await coacheeService.get(targetClientId);
        const currentCount = coachee?.bookedSessions ?? 0;
        const {
          _id: _ignored,
          id: _ignoredId,
          __v: _ignoredV,
          ...coacheeData
        } = coachee as any;
        const updated = await coacheeService.save({
          ...coacheeData,
          id: targetClientId,
          bookedSessions: currentCount + 1,
        });

        if (!updated?.id) return;

        const updatedId = updated.id.toString();
        setCoachees((prev) => {
          let found = false;
          const next = prev.map((c) => {
            if ((c.id || "").toString() === updatedId) {
              found = true;
              return {
                ...c,
                bookedSessions: updated.bookedSessions ?? currentCount + 1,
              };
            }
            return c;
          });
          return found ? next : [...next, updated];
        });
      } catch (err) {
        console.error("Failed to increment coachee booked sessions", err);
      }
    },
    [],
  );

  const parseDateTime = () => {
    if (!date || !time) return null;
    const [hoursStr, minutesStr] = time.split(":");
    const [yearStr, monthStr, dayStr] = date.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if ([year, month, day, hours, minutes].some((n) => Number.isNaN(n)))
      return null;
    // Build UTC date to avoid local timezone shifting the wall-clock time
    const start = new Date(Date.UTC(year, month - 1, day, hours, minutes));
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + duration * 60000);
    return { start, end };
  };

  const validate = () => {
    if (!name.trim() || !clientId || !date || !time) {
      return false;
    }
    const parsed = parseDateTime();
    return Boolean(parsed);
  };

  const handleSave = async () => {
    if (!validate()) return;
    const parsed = parseDateTime();
    if (!parsed) return;

    setSaving(true);
    const creatorEmail = (appContext.state.invoker?.email || "").trim();
    const payload: Session = {
      id: initialSession?.id,
      name: name.trim(),
      clientId,
      startTime: parsed.start,
      endTime: parsed.end,
      duration: Math.max(
        5,
        Math.round((parsed.end.getTime() - parsed.start.getTime()) / 60000),
      ),
      location: location.trim() || undefined,
      remarks: remarks.trim()
        ? remarks.split(/\r?\n/).filter(Boolean)
        : undefined,
      status: initialSession?.status || SessionStatus.Planned,
      creator: initialSession?.creator || creatorEmail || undefined,
    };

    try {
      const saved = await sessionService.save(payload);
      if (!initialSession && clientId) {
        await incrementBookedSessions(clientId);
      }
      if (initialSession) {
        onSaved?.(saved);
      } else {
        onCreated(saved);
      }
      setSuccessSnackbarOpen(true);
      resetForm();
      onClose();
    } catch (err) {
      console.error("Failed to save session", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleWarningClose}
        fullWidth
        maxWidth="sm" slotProps={{ paper: { sx: { backgroundColor: "white" } } }}
      >
        <DialogTitle>
          {initialSession
            ? t("coachingNavigator.sessionDialog.editTitle")
            : t("coachingNavigator.createSession")}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2.5}>
            <TextField
              required
              label={t("coachingNavigator.name")}
              placeholder={t("coachingNavigator.sessionForm.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }}
            />

            <FormControl
              fullWidth
              required
              sx={{ backgroundColor: "white", borderRadius: 1 }}
            >
              <InputLabel>{t("coachingNavigator.client")}</InputLabel>
              <Select
                value={clientId}
                label={t("coachingNavigator.client")}
                MenuProps={{ slotProps: { paper: {
                    sx: { backgroundColor: "#f5f5f5" },
                  } } }}
                onChange={(e) => {
                  const value = e.target.value as string;
                  if (value === "__create_new_client__") {
                    setCreateClientOpen(true);
                    return;
                  }
                  setClientId(value);
                }}
              >
                {coachees.map((c) => (
                  <MenuItem key={c.id} value={c.id || ""}>
                    {c.name}
                  </MenuItem>
                ))}
                {coachees.length > 0 && <Divider />}
                <MenuItem
                  value="__create_new_client__"
                  sx={{ color: "primary.main", fontWeight: 500 }}
                >
                  <PersonAddAltIcon sx={{ mr: 1, fontSize: "1.25rem" }} />
                  {t("coachingNavigator.addCoachee") || "Create New Client"}
                </MenuItem>
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
                alignItems: "center",
              }}
            >
              <TextField
                required
                type="date"
                label={t("coachingNavigator.date")}
                slotProps={{ inputLabel: { shrink: true } }}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <TextField
                required
                type="time"
                label={t("coachingNavigator.time")}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 300 } }}
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 220,
                }}
              >
                <IconButton
                  onClick={() => handleDurationChange(-5)}
                  disabled={duration <= 5}
                  sx={{ border: 1, borderColor: "divider" }}
                  aria-label={t("coachingNavigator.decrease")}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  value={formatDurationDisplay(duration)}
                  sx={{ flex: 1, minWidth: 80 }} slotProps={{ input: { readOnly: true, sx: { textAlign: "center" } }, htmlInput: { style: { textAlign: "center" } } }}
                />
                <IconButton
                  onClick={() => handleDurationChange(5)}
                  disabled={duration >= 480}
                  sx={{ border: 1, borderColor: "divider" }}
                  aria-label={t("coachingNavigator.increase")}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>

            <TextField
              label={t("coachingNavigator.location")}
              placeholder={t(
                "coachingNavigator.sessionForm.locationPlaceholder",
              )}
              value={location}
              onChange={(e) => setLocation(e.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }}
            />

            <TextField
              label={t("coachingNavigator.remarks")}
              placeholder={t(
                "coachingNavigator.sessionForm.remarksPlaceholder",
              )}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              multiline
              minRows={4} slotProps={{ htmlInput: { maxLength: 1000 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={handleWarningClose}>
            {t("button.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!validate() || saving}
          >
            {initialSession
              ? t("coachingNavigator.sessionDialog.save")
              : t("button.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateClientPanel
        open={createClientOpen}
        onClose={() => setCreateClientOpen(false)}
        onCreated={handleClientCreated}
      />

      <ConfirmationPanel
        action="warning"
        showModal={warningDialogOpen}
        hideModal={() => setWarningDialogOpen(false)}
        confirmModal={() => {
          setWarningDialogOpen(false);
          resetForm();
          onClose();
        }}
      />

      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={2500}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
          icon={<CheckIcon fontSize="inherit" />}
        >
          {initialSession
            ? t("coachingNavigator.sessionDialog.saved")
            : t("coachingNavigator.sessionDialog.created")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateSessionPanel;
